import {
  BufferAttribute,
  DoubleSide,
  Bone,

  Color,
  Group,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Skeleton,
  SkinnedMesh,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  type AnimationClip,
} from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import { fitSkinUnderGarment } from './tuckSkinUnder';
import {
  HAIR_COLORS,
  type CharacterConfig,
  type HairColor,
  type HairstyleId,
  type BeardId,
  type EyebrowsId,
  type OutfitId,
  type SkinTone,
} from './CharacterDefinition';

export interface CharacterInstance {
  root: Group;
  bodyMesh: SkinnedMesh;
  hairMesh: Object3D | null;
  beardMesh: Object3D | null;
  eyebrowsMesh: Object3D | null;
  outfitGroup: Object3D | null;
  animations: Map<string, AnimationClip>;
  config: CharacterConfig;
  setHairstyle: (hairId: HairstyleId) => Promise<void>;
  setBeard: (beardId: BeardId) => Promise<void>;
  setEyebrows: (eyebrowsId: EyebrowsId) => Promise<void>;
  setSkinTone: (tone: SkinTone) => Promise<void>;
  setHairColor: (color: HairColor) => void;
  setOutfit: (outfitId: OutfitId, hood?: boolean) => Promise<void>;
  setHood: (hood: boolean) => void;
  dispose: () => void;
}

export class CharacterLoader {
  private readonly gltfLoader = new GLTFLoader();
  private readonly textureLoader = new TextureLoader();
  private readonly gltfCache = new Map<string, GLTF>();
  private readonly textureCache = new Map<string, Texture>();
  private animationClips: AnimationClip[] | null = null;

  async loadGLTF(url: string): Promise<GLTF> {
    let cached = this.gltfCache.get(url);
    if (!cached) {
      cached = await this.gltfLoader.loadAsync(url);
      this.gltfCache.set(url, cached);
    }
    const scene = SkeletonUtils.clone(cached.scene) as Group;

    // SkeletonUtils.clone sdílí materiály s originálem v cache. Bez vlastní
    // kopie by obarvení vlasů nebo pleti přeteklo do všech dalších instancí
    // téhož modelu — a protože cache přežije i přezbrojení, i do těch příštích.
    // Textury zůstávají sdílené, takže to nestojí ani bajt VRAM navíc.
    scene.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      object.material = Array.isArray(object.material)
        ? object.material.map((m) => m.clone())
        : object.material.clone();
    });

    return { animations: cached.animations, scene } as GLTF;
  }

  /**
   * Textury pleti se načítají mimo glTF, protože se přepínají za běhu.
   * Cesty proto míří do sdílené složky, kterou vyrábí `tools/pack-assets.mjs`
   * — kdyby zůstaly u původních .png vedle modelů, po kompilaci assetů
   * by se rozešly a pleť by zmizela.
   */
  async loadTexture(url: string, isColor = true): Promise<Texture> {
    const cached = this.textureCache.get(url);
    if (cached) return cached;

    const tex = await this.textureLoader.loadAsync(url);
    if (isColor) tex.colorSpace = SRGBColorSpace;
    tex.flipY = false; // glTF standard uses non-flipped UV coordinates
    this.textureCache.set(url, tex);
    return tex;
  }

  /**
   * Klipy se skládají z několika souborů.
   *
   * Základ je Quaternius UAL1. Rybářské animace pocházejí z KayKit (CC0),
   * kde běží na úplně jiném rigu — přenesené na náš je vyrábí
   * `tools/retarget-fishing.py`. Kosti se v nich jmenují stejně, takže je
   * stačí přisypat do stejné mapy a mixér je bere jako své vlastní.
   */
  async getAnimations(): Promise<Map<string, AnimationClip>> {
    if (!this.animationClips) {
      const files = [
        '/models/animations/UAL1_Standard.glb',
        '/models/animations/UAL2_Standard.glb',
        '/models/animations/Fishing.glb',
      ];
      const loaded = await Promise.all(
        files.map((f) =>
          this.loadGLTF(f).then(
            (g) => g.animations,
            (error) => {
              console.warn(`[anim] ${f} se nepodařilo načíst:`, error);
              return [] as AnimationClip[];
            },
          ),
        ),
      );
      this.animationClips = loaded.flat();
    }
    const map = new Map<string, AnimationClip>();
    for (const clip of this.animationClips) {
      map.set(clip.name, clip);
    }
    return map;
  }

  async createCharacter(config: CharacterConfig): Promise<CharacterInstance> {
    const animations = await this.getAnimations();
    const root = new Group();
    root.name = `character_${config.gender}`;

    const bodyFile =
      config.gender === 'male'
        ? '/models/character/Superhero_Male_FullBody.gltf'
        : '/models/character/Superhero_Female_FullBody.gltf';

    const bodyGLTF = await this.loadGLTF(bodyFile);
    const bodyScene = bodyGLTF.scene;
    root.add(bodyScene);

    const applyMaterialSettings = (mesh: SkinnedMesh) => {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((mat) => {
        if (mat instanceof MeshStandardMaterial) {
          mat.vertexColors = false;
          // Nepřepisovat hodnoty, které přišly z ORM textury — jen doplnit,
          // když materiál žádnou mapu nemá. Natvrdo nastavené metalness = 0
          // jinak úplně vyřadí kovové části (přezky, kování).
          if (!mat.roughnessMap) mat.roughness = 0.85;
          if (!mat.metalnessMap) mat.metalness = 0.0;
          mat.needsUpdate = true;
        }
      });
    };

    /**
     * Základní postava obsahuje tři SkinnedMeshe a jejich uzly se jmenují
     * `Eyebrows`, `Eyes` a `SuperHero_Male`. Rozpoznávat je podle jména uzlu
     * je křehké (u ženské varianty se liší), proto je poznáváme podle jména
     * materiálu, které Quaternius drží konzistentní:
     * `MI_Superhero_*` = tělo, `MI_Eyes` = oči, `MI_Hair_*` = zapečené obočí.
     */
    const materialNamesOf = (mesh: Mesh): string =>
      (Array.isArray(mesh.material) ? mesh.material : [mesh.material])
        .map((m) => m.name ?? '')
        .join(' ');

    let bodyMesh: SkinnedMesh | null = null;
    const bakedHairMeshes: SkinnedMesh[] = [];

    bodyScene.traverse((child) => {
      if (!(child instanceof SkinnedMesh)) return;
      applyMaterialSettings(child);

      const materialNames = materialNamesOf(child);
      if (materialNames.includes('Superhero')) {
        bodyMesh = child;
      } else if (materialNames.includes('Hair')) {
        bakedHairMeshes.push(child);
      }
    });

    // Zapečené obočí se skryje, aby ho nahradily modulární díly. Oči se
    // nechávají — pro ty žádná modulární varianta neexistuje.
    for (const mesh of bakedHairMeshes) mesh.visible = false;

    if (!bodyMesh) {
      throw new Error(
        `V ${bodyFile} se nenašel SkinnedMesh s materiálem MI_Superhero_*. ` +
          `Nalezené meshe: ${bodyScene.children.map((c) => c.name).join(', ')}`,
      );
    }

    const mainBodyMesh: SkinnedMesh = bodyMesh;
    // Outfity se přidávají ke stejnému rodiči jako tělo, aby měly totožnou
    // světovou matici — na tom stojí správnost skinningu (viz setOutfit).
    const bodyParent: Object3D = mainBodyMesh.parent ?? bodyScene;

    // Z těla se kreslí jen hlava s krkem.
    //
    // Outfit tělo nenahrazuje jen opticky — je na to postavený. Readme packu:
    // "only the head of the model is required. Using the full body will result
    // in clipping." Zkoušel jsem nechat tělo celé a jen ho zatlačit pod látku;
    // nešlo to: 1588 vrcholů ze 7376 zůstalo venku, nejvíc o 24,8 cm, protože
    // Superhero trup se do toho oblečení prostě nevejde.
    const fullBodyIndex = mainBodyMesh.geometry.index;
    let headOnlyIndex: BufferAttribute | null = null;

    if (fullBodyIndex && mainBodyMesh.geometry.attributes.skinIndex) {
      const skinIndex = mainBodyMesh.geometry.attributes.skinIndex;
      const headBoneIndices = new Set<number>();
      mainBodyMesh.skeleton.bones.forEach((b, idx) => {
        if (b.name === 'Head' || b.name === 'neck_01') headBoneIndices.add(idx);
      });

      // Váhy se musí kontrolovat spolu s indexy. Nepoužité sloty mají
      // v glTF index 0 a váhu 0 — kdyby kost 0 byla zrovna Head, spadlo
      // by do hlavy celé tělo.
      const skinWeight = mainBodyMesh.geometry.attributes.skinWeight;
      const MIN_WEIGHT = 0.02;

      const isHeadVertex = new Uint8Array(skinIndex.count);
      for (let i = 0; i < skinIndex.count; i++) {
        const indices = [skinIndex.getX(i), skinIndex.getY(i), skinIndex.getZ(i), skinIndex.getW(i)];
        const weights = skinWeight
          ? [skinWeight.getX(i), skinWeight.getY(i), skinWeight.getZ(i), skinWeight.getW(i)]
          : [1, 1, 1, 1];
        let headWeight = 0;
        for (let sl = 0; sl < 4; sl++) {
          if (headBoneIndices.has(indices[sl])) headWeight += weights[sl];
        }
        if (headWeight >= MIN_WEIGHT) isHeadVertex[i] = 1;
      }

      // Trojúhelník stačí, aby se hlavy dotýkal jediným vrcholem — region
      // tím o prstenec přeteče a schová se pod oblečením. Podmínka "všechny
      // tři" ho naopak zmenší a na krku vznikne zubatá díra.
      const headIndices: number[] = [];
      for (let i = 0; i < fullBodyIndex.count; i += 3) {
        const a = fullBodyIndex.getX(i);
        const b = fullBodyIndex.getX(i + 1);
        const c = fullBodyIndex.getX(i + 2);
        if (isHeadVertex[a] || isHeadVertex[b] || isHeadVertex[c]) headIndices.push(a, b, c);
      }

      const TypedArray = fullBodyIndex.array instanceof Uint32Array ? Uint32Array : Uint16Array;
      headOnlyIndex = new BufferAttribute(new TypedArray(headIndices), 1);
    }

    const headGeom = mainBodyMesh.geometry.clone();
    headGeom.clearGroups();
    if (headOnlyIndex) headGeom.setIndex(headOnlyIndex);

    // Klidové pozice si držíme stranou. Zatlačování pod látku je mění na
    // místě a při každém převlečení se musí vyjít znovu z originálu — jinak
    // by se krk s každou výměnou zanořil hlouběji do sebe.
    const headRestPositions = new Float32Array(
      headGeom.getAttribute('position').array as Float32Array,
    );

    /**
     * Postaví přidaný skinned díl přesně tak, jak stojí tělo.
     *
     * Skinning ve three.js počítá `meshWorld * bindMatrixInverse * boneWorld *
     * boneInverse * bindMatrix * vertex`. Aby se první dva členy vyrušily,
     * musí `bindMatrix` odpovídat světové matici meshe — a ta se musí chovat
     * stejně jako u těla i poté, co se postava rozejde po světě.
     *
     * Proto: stejný rodič, stejná lokální transformace, a bind matrix převzatá
     * z těla. Použít místo ní živou `matrixWorld` znamená, že se pohyb postavy
     * do skinningu započítá podruhé a díl odletí mimo obraz.
     */
    const placeLikeBody = (object: Object3D): void => {
      object.position.copy(mainBodyMesh.position);
      object.quaternion.copy(mainBodyMesh.quaternion);
      object.scale.copy(mainBodyMesh.scale);
      bodyParent.add(object);
    };

    // Lebka. Zatlačování kůže pod látku se jí musí vyhnout — u vysokého
    // límce hraničáře je brada blíž k látce než ke krku a bez téhle pojistky
    // by ji to stáhlo dovnitř límce.
    const skullBoneIndices = mainBodyMesh.skeleton.bones
      .map((bone, i) => (bone.name === 'Head' ? i : -1))
      .filter((i) => i >= 0);

    /**
     * Hlava a krk — jediná část těla, která se pod oblečením kreslí.
     *
     * Materiál je **oboustranný**. Řez tělem nutně nechá na svém okraji
     * otvor a při jednostranném vykreslení se skrz něj dívalo dovnitř
     * postavy: z podhledu vypadala děravě a průsvitná. Oboustranně se na
     * tom místě vykreslí rub kůže, takže tam není vidět nic než kůže.
     * Stojí to jen vypnutý culling na jednom malém meshi.
     */
    const headMaterial = Array.isArray(mainBodyMesh.material)
      ? mainBodyMesh.material.map((m) => {
          const c = m.clone();
          c.side = DoubleSide;
          return c;
        })
      : (() => {
          const c = mainBodyMesh.material.clone();
          c.side = DoubleSide;
          return c;
        })();

    const headSkinMesh = new SkinnedMesh(headGeom, headMaterial);
    headSkinMesh.name = 'HeadSkinMesh';
    headSkinMesh.castShadow = true;
    headSkinMesh.receiveShadow = true;
    headSkinMesh.frustumCulled = false;
    headSkinMesh.visible = false;
    placeLikeBody(headSkinMesh);
    headSkinMesh.bind(mainBodyMesh.skeleton, mainBodyMesh.bindMatrix);

    bodyScene.updateMatrixWorld(true);
    const headBone = bodyScene.getObjectByName('Head') as Bone | undefined;
    const invHeadMatrix = headBone ? headBone.matrixWorld.clone().invert() : new Matrix4();

    const currentConfig: CharacterConfig = { ...config };
    let hairMesh: Object3D | null = null;
    let beardMesh: Object3D | null = null;
    let eyebrowsMesh: Object3D | null = null;
    let outfitGroup: Object3D | null = null;

    const attachHeadPart = async (url: string): Promise<Object3D | null> => {
      try {
        const gltf = await this.loadGLTF(url);
        const partScene = gltf.scene;
        partScene.updateMatrixWorld(true);

        const group = new Group();

        partScene.traverse((child) => {
          if (child instanceof Mesh) {
            const meshClone = child.clone();
            meshClone.castShadow = true;
            meshClone.receiveShadow = true;
            meshClone.frustumCulled = false;
            
            // 1. Bake node transform/scale (fixes scale in Hair_Buns)
            meshClone.geometry = child.geometry.clone();
            meshClone.geometry.applyMatrix4(child.matrixWorld);
            // 2. Transform into local Head bone space
            meshClone.geometry.applyMatrix4(invHeadMatrix);

            meshClone.position.set(0, 0, 0);
            meshClone.rotation.set(0, 0, 0);
            meshClone.scale.set(1, 1, 1);

            const mats = Array.isArray(meshClone.material) ? meshClone.material : [meshClone.material];
            mats.forEach((mat) => {
              if (mat instanceof MeshStandardMaterial) {
                mat.vertexColors = false;
                mat.roughness = 0.85;
                mat.metalness = 0.0;
                mat.needsUpdate = true;
              }
            });
            group.add(meshClone);
          }
        });

        if (group.children.length === 0) return null;

        if (headBone) {
          headBone.add(group);
        } else {
          bodyScene.add(group);
        }
        return group;
      } catch (err) {
        console.warn(`Failed to load part from ${url}:`, err);
        return null;
      }
    };

    const setSkinTone = async (tone: SkinTone) => {
      currentConfig.skinTone = tone;
      const isMale = currentConfig.gender === 'male';
      // Use the dark base texture which has clean black underwear for both tones
      const texPath = isMale
        ? '/models/textures/T_Superhero_Male_Dark.webp'
        : '/models/textures/T_Superhero_Female_Dark_BaseColor.webp';

      const skinTex = await this.loadTexture(texPath, true);
      // For light skin, multiply with a natural warm Caucasian skin tone (black underwear stays black!)
      const skinColor = tone === 'light' ? new Color(0xffeedd) : new Color(0x9a8070);

      bodyScene.traverse((child) => {
        if (child instanceof SkinnedMesh) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const m of mats) {
            if (m instanceof MeshStandardMaterial && m.name.includes('Superhero')) {
              m.map = skinTex;
              m.color.copy(skinColor);
              m.needsUpdate = true;
            }
          }
        }
      });

      if (outfitGroup) {
        outfitGroup.traverse((child) => {
          if (child instanceof Mesh) {
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            for (const m of mats) {
              if (m instanceof MeshStandardMaterial && m.name.includes('Regular')) {
                m.color.copy(skinColor);
                m.needsUpdate = true;
              }
            }
          }
        });
      }
    };

    const setHairColor = (color: HairColor) => {
      currentConfig.hairColor = color;
      const colorDef = HAIR_COLORS.find((c) => c.id === color) ?? HAIR_COLORS[1];
      const hairCol = new Color(colorDef.hex);

      // Tint base model's hairline/eyebrows
      bodyScene.traverse((child) => {
        if (child instanceof SkinnedMesh) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const m of mats) {
            if (m instanceof MeshStandardMaterial && m.name.includes('Hair')) {
              m.color.copy(hairCol);
              m.needsUpdate = true;
            }
          }
        }
      });

      const tintPart = (part: Object3D | null) => {
        if (!part) return;
        part.traverse((child) => {
          if (child instanceof Mesh) {
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            for (const m of mats) {
              if (m instanceof MeshStandardMaterial) {
                m.color.copy(hairCol);
                m.needsUpdate = true;
              }
            }
          }
        });
      };

      tintPart(hairMesh);
      tintPart(beardMesh);
      tintPart(eyebrowsMesh);
    };

    const setHairstyle = async (hairId: HairstyleId) => {
      currentConfig.hairstyle = hairId;
      if (hairMesh) {
        hairMesh.removeFromParent();
        hairMesh = null;
      }
      if (hairId !== 'none') {
        hairMesh = await attachHeadPart(`/models/hairstyles/${hairId}.gltf`);
        setHairColor(currentConfig.hairColor);
      }
    };

    const setBeard = async (beardId: BeardId) => {
      currentConfig.beard = beardId;
      if (beardMesh) {
        beardMesh.removeFromParent();
        beardMesh = null;
      }
      if (beardId !== 'none') {
        beardMesh = await attachHeadPart(`/models/hairstyles/${beardId}.gltf`);
        setHairColor(currentConfig.hairColor);
      }
    };

    const setEyebrows = async (eyebrowsId: EyebrowsId) => {
      currentConfig.eyebrows = eyebrowsId;
      if (eyebrowsMesh) {
        eyebrowsMesh.removeFromParent();
        eyebrowsMesh = null;
      }
      if (eyebrowsId !== 'none') {
        eyebrowsMesh = await attachHeadPart(`/models/hairstyles/${eyebrowsId}.gltf`);
        setHairColor(currentConfig.hairColor);
      }
    };

    const setOutfit = async (outfitId: OutfitId, hood = currentConfig.hood) => {
      currentConfig.outfit = outfitId;
      currentConfig.hood = hood;

      if (outfitGroup) {
        // Skupina visí na rodiči těla, ne na bodyScene — odebírat ji přes
        // bodyScene.remove() by ji ve scéně nechalo viset.
        outfitGroup.removeFromParent();
        // Geometrie a materiály jsou klony, o které se nikdo jiný nedělí.
        // Bez uvolnění by každé přezbrojení nechalo ve VRAM celý outfit.
        outfitGroup.traverse((child) => {
          if (!(child instanceof Mesh)) return;
          child.geometry.dispose();
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const m of mats) m.dispose();
        });
        outfitGroup = null;
      }

      if (outfitId === 'none') {
        // Show full superhero muscular body
        mainBodyMesh.visible = true;
        headSkinMesh.visible = false;
        // Vrátit krk do původního tvaru, ať ho příští outfit nedostane
        // už jednou zamáčknutý.
        fitSkinUnderGarment(headGeom, headRestPositions, [], skullBoneIndices);
        return;
      }

      // Hide superhero muscular body and show only clean head & neck
      mainBodyMesh.visible = false;
      headSkinMesh.visible = true;

      const isMale = currentConfig.gender === 'male';
      let outfitPath = '';
      if (outfitId === 'peasant') {
        outfitPath = isMale
          ? '/models/outfits/Outfits/Male_Peasant.gltf'
          : '/models/outfits/Outfits/Female_Peasant.gltf';
      } else if (outfitId === 'ranger') {
        outfitPath = isMale
          ? '/models/outfits/Outfits/Male_Ranger.gltf'
          : '/models/outfits/Outfits/Female_Ranger.gltf';
      }

      try {
        const outfitGLTF = await this.loadGLTF(outfitPath);
        const outfitScene = outfitGLTF.scene;
        outfitScene.updateMatrixWorld(true);

        const group = new Group();
        group.name = `outfit_${outfitId}`;

        // Jméno kosti -> její pořadí v kostře těla. Potřebujeme index, ne jen
        // kost, protože se k ní musí vzít i odpovídající bind matice (níž).
        const boneIndex = new Map<string, number>();
        mainBodyMesh.skeleton.bones.forEach((b, i) => boneIndex.set(b.name, i));

        const skinnedMeshes: SkinnedMesh[] = [];
        outfitScene.traverse((child) => {
          if (child instanceof SkinnedMesh) {
            skinnedMeshes.push(child);
          }
        });

        // Skupina stojí přesně tam, kde tělo — díky tomu má každý díl uvnitř
        // (s jednotkovou lokální transformací) totožnou světovou matici jako
        // tělo, což je podmínka správného skinningu.
        placeLikeBody(group);

        let unmatchedTotal = 0;

        skinnedMeshes.forEach((skinnedClone) => {
          skinnedClone.castShadow = true;
          skinnedClone.receiveShadow = true;
          skinnedClone.frustumCulled = false;

          // Kosti se párují podle jména na kostru těla. Spolu s kostí se ale
          // musí převzít i její **bind matice** — a právě na tom to dřív
          // padalo.
          //
          // Mužské outfity z packu jsou narigované na *Regular* tělo, kdežto
          // hra používá *Superhero*, které má o 2,4 cm širší boky a o 1,7 cm
          // jinak posazená ramena. Když se vzaly kosti z jednoho rigu a bind
          // matice z druhého, jejich součin nebyl v klidové póze jednotkový a
          // oblečení se v základním postoji roztáhlo: nohavice a boty odjely
          // o 2,4 cm od sebe, rukávy o 1,8 cm. (Ženské rigy jsou shodné, tam
          // se nikdy nic neprojevilo — proto ta nesouměrnost.)
          //
          // Řešení je standardní přepis klidové pózy: bind matice se vezmou
          // z těla, takže `boneWorld * boneInverse` je v klidu jednotkové a
          // outfit se vykreslí přesně tak, jak byl vymodelovaný. Nesmí se
          // brát ze živé `matrixWorld` — postava už může být rozanimovaná,
          // třeba když si vzdálený hráč mění výstroj za běhu.
          const sourceBones = skinnedClone.skeleton.bones;
          const sourceInverses = skinnedClone.skeleton.boneInverses;
          const remappedBones: Bone[] = [];
          const remappedInverses: Matrix4[] = [];

          sourceBones.forEach((bone, i) => {
            const index = boneIndex.get(bone.name);
            if (index === undefined) {
              // Nenapárovaná kost si ponechá tu původní i s její maticí —
              // počet kostí musí sedět, jinak se skinning rozsype.
              unmatchedTotal++;
              remappedBones.push(bone);
              remappedInverses.push(sourceInverses[i]);
              return;
            }
            remappedBones.push(mainBodyMesh.skeleton.bones[index]);
            remappedInverses.push(mainBodyMesh.skeleton.boneInverses[index]);
          });

          skinnedClone.position.set(0, 0, 0);
          skinnedClone.quaternion.identity();
          skinnedClone.scale.set(1, 1, 1);
          group.add(skinnedClone);

          skinnedClone.bind(new Skeleton(remappedBones, remappedInverses), mainBodyMesh.bindMatrix);

          const mats = Array.isArray(skinnedClone.material)
            ? skinnedClone.material
            : [skinnedClone.material];
          mats.forEach((mat) => {
            if (mat instanceof MeshStandardMaterial) {
              mat.vertexColors = false;
              if (!mat.roughnessMap) mat.roughness = 0.85;
              if (!mat.metalnessMap) mat.metalness = 0.0;
              mat.needsUpdate = true;
            }
          });

          if (skinnedClone.name.includes('Hood')) {
            skinnedClone.visible = currentConfig.hood;
          }
        });

        if (unmatchedTotal > 0) {
          console.warn(
            `[outfit] ${outfitId}: ${unmatchedTotal} kostí se nepodařilo napárovat na kostru těla — ` +
              `díl se nebude hýbat s postavou.`,
          );
        }

        outfitGroup = group;

        // Superhero trup je mohutnější než Regular, na kterém pack outfity
        // stavěl — bez tohohle kroku prorazí kůže látku na horní části zad.
        // Bere se jen látka; holé ruce, které pack přibaluje s materiálem
        // MI_Regular_*, jsou taky kůže a neměly by nic zatlačovat.
        //
        // Kápě se musí vynechat, i když je látka: sahá kolem osy těla až nad
        // temeno, takže by se z ní stal "límec" ve výšce hlavy a pod látku by
        // se zatáhl celý obličej.
        //
        // Kápě se vynechává, i když je látka: sahá kolem osy těla až nad
        // temeno, takže by se pod ni zatáhl obličej.
        //
        // Holé ruce, které pack k outfitu přibaluje, se naopak berou taky —
        // zakrývají tělo úplně stejně jako látka. Bez nich by se předloktí
        // postavy potkalo s předloktím z outfitu a obě by se praly o stejné
        // pixely.
        const cloth = skinnedMeshes.filter((mesh) => {
          if (!mesh.visible || mesh.name.includes('Hood')) return false;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          return !mats.some((m) => (m.name ?? '').includes('Regular'));
        });
        fitSkinUnderGarment(headGeom, headRestPositions, cloth, skullBoneIndices);

        await setSkinTone(currentConfig.skinTone);
      } catch (err) {
        console.warn(`Failed to load outfit from ${outfitPath}:`, err);
      }
    };

    const setHood = (hood: boolean) => {
      currentConfig.hood = hood;
      if (outfitGroup) {
        outfitGroup.traverse((child) => {
          if (child.name.includes('Hood')) {
            child.visible = hood;
          }
        });
      }
    };

    // Initialize skin tone, outfit & parts
    await setSkinTone(config.skinTone);
    await setOutfit(config.outfit, config.hood);
    await setHairstyle(config.hairstyle);
    if (config.gender === 'male' && config.beard !== 'none') {
      await setBeard(config.beard);
    }
    await setEyebrows(config.eyebrows);
    setHairColor(config.hairColor);

    const instance: CharacterInstance = {
      root,
      bodyMesh,
      get hairMesh() {
        return hairMesh;
      },
      get beardMesh() {
        return beardMesh;
      },
      get eyebrowsMesh() {
        return eyebrowsMesh;
      },
      get outfitGroup() {
        return outfitGroup;
      },
      animations,
      config: currentConfig,
      setHairstyle,
      setBeard,
      setEyebrows,
      setSkinTone,
      setHairColor,
      setOutfit,
      setHood,
      dispose() {
        root.removeFromParent();
      },
    };

    return instance;
  }
}
